import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, Download, Loader2, KeyRound } from "lucide-react";
import { C, Panel, PriorityBadge, Empty, NotConnectedNote, downloadCsv } from "./seoUi";
import { errMsg } from "@/lib/api";

const STATUS = ["planned", "in_progress", "published", "needs_update"];
const PRIORITY = ["high", "medium", "low"];

const Th = ({ children, className = "" }) => (
  <th className={`text-left font-semibold px-3 py-2 text-[12px] uppercase tracking-wide ${className}`} style={{ color: C.slate }}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-3 py-2 text-[13px] align-top ${className}`} style={{ color: C.charcoal }}>{children}</td>
);

const empty = {
  keyword: "", keyword_group: "", priority: "medium", content_status: "planned",
  manual_search_volume: "", manual_difficulty: "", manual_current_rank: "", manual_target_rank: "",
  service_lane: "", target_page_url: "", territory_label: "", search_intent: "", notes: "", franchise_id: "",
};

export default function KeywordsPanel({ franchise, meta }) {
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/seo-intelligence/keywords", { params: franchise ? { franchise } : {} });
      setItems(data.items || []); setGroups(data.keyword_groups || []);
    } catch { toast.error("Could not load keywords."); }
    finally { setLoading(false); }
  }, [franchise]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const num = (v) => (v === "" || v === null ? null : Number(v));

  const create = async () => {
    if (!form.keyword.trim()) { toast.error("Keyword is required."); return; }
    setSaving(true);
    try {
      await api.post("/seo-intelligence/keywords", {
        ...form,
        franchise_id: form.franchise_id || (franchise || null),
        manual_search_volume: num(form.manual_search_volume), manual_difficulty: num(form.manual_difficulty),
        manual_current_rank: num(form.manual_current_rank), manual_target_rank: num(form.manual_target_rank),
      });
      toast.success("Keyword added.");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(errMsg(e, "Could not add keyword.")); }
    finally { setSaving(false); }
  };

  const patch = async (id, body) => {
    try { await api.patch(`/seo-intelligence/keywords/${id}`, body); setItems((xs) => xs.map((x) => x.id === id ? { ...x, ...body } : x)); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Remove this keyword?")) return;
    try { await api.delete(`/seo-intelligence/keywords/${id}`); setItems((xs) => xs.filter((x) => x.id !== id)); toast.success("Removed."); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const exportCsv = () => downloadCsv("seo-keywords.csv", items.map((k) => ({
    keyword: k.keyword, group: k.keyword_group, priority: k.priority, status: k.content_status,
    search_volume: k.manual_search_volume, difficulty: k.manual_difficulty, current_rank: k.manual_current_rank,
    target_rank: k.manual_target_rank, service_lane: k.service_lane, target_page: k.target_page_url, territory: k.territory_label,
  })));

  return (
    <Panel title="Keyword Planner" testid="seo-tab-keywords"
      subtitle="Manual entry only — search volume, difficulty and rank are your own values (no fabricated metrics)."
      right={<div className="flex items-center gap-2">
        <Button data-testid="keywords-export-btn" size="sm" variant="outline" onClick={exportCsv} className="h-9 gap-1 border-[#D6E0E7]" style={{ color: C.navy }}><Download size={14} /> CSV</Button>
        <Button data-testid="add-keyword-btn" size="sm" onClick={() => setOpen(true)} className="h-9 gap-1 bg-[#9C1E22] hover:bg-[#861A1D] text-white"><Plus size={14} /> Add keyword</Button>
      </div>}>
      <NotConnectedNote>Automatic volume/difficulty/ranking data will appear here once Semrush or Google Search Console is connected. For now, add target keywords and track them manually.</NotConnectedNote>
      {loading ? <div className="py-10 flex justify-center"><Loader2 className="animate-spin" style={{ color: C.navy }} /></div> : items.length ? (
        <div className="overflow-x-auto rounded-lg border mt-3" style={{ borderColor: C.line }}>
          <table className="w-full border-collapse">
            <thead><tr style={{ background: "#F4F8FB" }}><Th>Keyword</Th><Th>Group</Th><Th>Priority</Th><Th>Status</Th><Th className="text-center">Vol.</Th><Th className="text-center">Diff.</Th><Th className="text-center">Rank</Th><Th>Target page</Th><Th></Th></tr></thead>
            <tbody>
              {items.map((k, i) => (
                <tr key={k.id} data-testid={`keyword-row-${i}`} className="border-t" style={{ borderColor: C.line }}>
                  <Td><span className="font-semibold" style={{ color: C.navy }}>{k.keyword}</span>{k.territory_label && <div className="text-[11px]" style={{ color: C.slate }}>{k.territory_label}</div>}</Td>
                  <Td><span className="text-[12px]">{k.keyword_group || "—"}</span></Td>
                  <Td>
                    <Select value={k.priority || "medium"} onValueChange={(v) => patch(k.id, { priority: v })}>
                      <SelectTrigger className="h-8 w-[110px] text-[12px]"><SelectValue /></SelectTrigger>
                      <SelectContent>{PRIORITY.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </Td>
                  <Td>
                    <Select value={k.content_status || "planned"} onValueChange={(v) => patch(k.id, { content_status: v })}>
                      <SelectTrigger className="h-8 w-[130px] text-[12px]"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </Td>
                  <Td className="text-center">{k.manual_search_volume ?? "—"}</Td>
                  <Td className="text-center">{k.manual_difficulty ?? "—"}</Td>
                  <Td className="text-center">{k.manual_current_rank ?? "—"}{k.manual_target_rank ? ` → ${k.manual_target_rank}` : ""}</Td>
                  <Td><span className="font-mono text-[11.5px]" style={{ color: C.sky }}>{k.target_page_url || "—"}</span></Td>
                  <Td><button data-testid={`keyword-del-${i}`} onClick={() => del(k.id)} className="text-[#9C1E22] hover:opacity-70"><Trash2 size={15} /></button></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <Empty>No keywords yet. Add your first target keyword.</Empty>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="keyword-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound size={17} style={{ color: C.navy }} /> Add target keyword</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            <div className="space-y-1"><Label>Keyword *</Label><Input data-testid="kw-keyword" value={form.keyword} onChange={(e) => set("keyword", e.target.value)} placeholder="e.g. senior move help kansas city" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Group</Label>
                <Select value={form.keyword_group} onValueChange={(v) => set("keyword_group", v)}>
                  <SelectTrigger data-testid="kw-group"><SelectValue placeholder="Select group" /></SelectTrigger>
                  <SelectContent>{groups.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITY.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {meta?.is_super && (meta?.franchises || []).length > 0 && (
              <div className="space-y-1"><Label>Territory (optional)</Label>
                <Select value={form.franchise_id} onValueChange={(v) => set("franchise_id", v)}>
                  <SelectTrigger data-testid="kw-franchise"><SelectValue placeholder="Global (no territory)" /></SelectTrigger>
                  <SelectContent>{meta.franchises.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Search volume</Label><Input data-testid="kw-vol" type="number" value={form.manual_search_volume} onChange={(e) => set("manual_search_volume", e.target.value)} placeholder="manual" /></div>
              <div className="space-y-1"><Label>Difficulty</Label><Input type="number" value={form.manual_difficulty} onChange={(e) => set("manual_difficulty", e.target.value)} placeholder="manual" /></div>
              <div className="space-y-1"><Label>Current rank</Label><Input type="number" value={form.manual_current_rank} onChange={(e) => set("manual_current_rank", e.target.value)} placeholder="manual" /></div>
              <div className="space-y-1"><Label>Target rank</Label><Input type="number" value={form.manual_target_rank} onChange={(e) => set("manual_target_rank", e.target.value)} placeholder="manual" /></div>
            </div>
            <div className="space-y-1"><Label>Target page URL</Label><Input value={form.target_page_url} onChange={(e) => set("target_page_url", e.target.value)} placeholder="/services/senior-living" /></div>
            <div className="space-y-1"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="kw-save" disabled={saving} onClick={create} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1">{saving && <Loader2 className="animate-spin" size={14} />} Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
